import { useState } from 'react'

import { Box, Radio, Section } from '@factorialco/deprecated-design-system'
import { AccessForSelectFragment, BlockedPeriodPolicyFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { AccessSelector } from 'modules/core/components/AccessSelector'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import LocationMultiselector from 'modules/timeoff/components/LocationMultiselector'
import TeamMultiselector from 'modules/timeoff/components/TeamMultiselector'
import AccessGroupStrategies from 'modules/timeoff/lib/BlockedPeriods/AccessGroupStrategies'
import type PolicyForm from 'modules/timeoff/lib/BlockedPeriods/PolicyUpdateForm'

interface Props {
  policy: BlockedPeriodPolicyFragment | undefined
  policyForm: PolicyForm
  loading: boolean
}

const Employees = ({ policy, policyForm: form, loading }: Props) => {
  const [selectedMembers, setMembers] = useState<AccessForSelectFragment[]>([])

  useEffect(() => {
    if (!loading && policy?.membersAsAccesses) {
      setMembers(policy.membersAsAccesses)
    }
  }, [loading, policy?.membersAsAccesses.length])

  const updateMembers = (members: AccessForSelectFragment[]) => {
    setMembers(members)
    form.setValues({ members: members.map((member) => member.id) })
  }

  return (
    <Section
      illustration="group"
      title={i18n.t('settings.timeoff_blocked_periods.detail.employees.title')}
      description={i18n.t('settings.timeoff_blocked_periods.detail.employees.description')}
    >
      <Radio.Wrap>
        <Box gap="s16">
          <Radio
            name="everyone"
            option={{
              label: i18n.t('settings.permissions.visibility.choices.everyone'),
              value: AccessGroupStrategies.everyone,
            }}
            description={i18n.t('settings.permissions.visibility.choices.everyone_desc')}
            value={form.get('strategy').value}
            onChange={(value) => form.get('strategy').set(value)}
          />
          <Radio
            name="dynamic_group"
            option={{
              label: i18n.t('settings.permissions.visibility.choices.dynamic_group'),
              value: AccessGroupStrategies.fqlMultiselect,
            }}
            description={i18n.t('settings.permissions.visibility.choices.dynamic_group_desc')}
            value={form.get('strategy').value}
            onChange={(value) => form.get('strategy').set(value)}
          >
            <Box gap="s12">
              <TeamMultiselector teamIds={policy?.teamIds} form={form} />
              <LocationMultiselector locationIds={policy?.locationIds} form={form} />
            </Box>
          </Radio>
          <Radio
            name="manual_selection"
            option={{
              label: i18n.t('settings.permissions.visibility.choices.manual_selection'),
              value: AccessGroupStrategies.members,
            }}
            description={i18n.t('settings.permissions.visibility.choices.manual_selection_desc')}
            value={form.get('strategy').value}
            onChange={(value) => form.get('strategy').set(value)}
          >
            {/* TODO: this should use an employee selector and not leak access details */}
            <AccessSelector value={selectedMembers} onSelect={updateMembers} multiple />
          </Radio>
        </Box>
      </Radio.Wrap>
    </Section>
  )
}

export default observer(Employees)
